// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import './styles.css';
import { loadDataset, searchIncidents, type Dataset, type SearchHit } from './data';
import type { ViewId } from './types';
import type { ViewContext } from './viewContext';
import { initTooltip } from './components/tooltip';
import { initGlossary } from './components/glossaryPopover';
import { createAbout } from './components/about';
import { createDrawers } from './components/drawer';
import { renderOverview } from './views/overview';
import { renderMap } from './views/map';
import { renderTime } from './views/time';
import { renderSpecies } from './views/species';
import { renderActivities } from './views/activities';
import { renderSeasonality } from './views/seasonality';
import { renderExplorer } from './views/explorer';
import { renderInsights } from './views/insights';
import { escapeHtml } from './utils/format';

// Nav tabs are words only — never count badges.
const VIEWS: Array<{ id: ViewId; label: string; render: (root: HTMLElement, ctx: ViewContext) => void }> = [
  { id: 'overview', label: 'Overview', render: renderOverview },
  { id: 'map', label: 'Map', render: renderMap },
  { id: 'time', label: 'Over time', render: renderTime },
  { id: 'species', label: 'Species', render: renderSpecies },
  { id: 'activities', label: 'Activities', render: renderActivities },
  { id: 'seasonality', label: 'Seasons', render: renderSeasonality },
  { id: 'explorer', label: 'Explore', render: renderExplorer },
  { id: 'insights', label: 'Insights', render: renderInsights },
];

const LOGO = `<svg viewBox="0 0 32 32" aria-hidden="true">
  <path d="M23.5 20.5C19 21 12.5 21 8.5 20.5c4.4-1 6.6-4.2 8.1-8.1 1.1-2.8 1.8-5.4 2.2-7.9.9 4.6 2.3 9.1 4.7 12.8.9 1.4 1.9 2.4 3 3.2-1 .1-2 .1-3 0z" fill="#0e7c86"/>
  <path d="M5 24.5c1.6 0 1.6-1.2 3.2-1.2s1.6 1.2 3.2 1.2 1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2 1.6-1.2 3.2-1.2 1.6 1.2 3.2 1.2" fill="none" stroke="#4fa9b3" stroke-width="1.7" stroke-linecap="round"/>
</svg>`;

function parseHash(): { view: ViewId | null; incident: number | null; state: string | null; species: string | null } {
  const h = location.hash.replace(/^#/, '');
  if (!h) return { view: null, incident: null, state: null, species: null };
  if (h.startsWith('i=')) return { view: null, incident: Number(h.slice(2)), state: null, species: null };
  if (h.startsWith('s=')) return { view: null, incident: null, state: h.slice(2), species: null };
  if (h.startsWith('sp=')) return { view: null, incident: null, state: null, species: h.slice(3) };
  const view = VIEWS.find((v) => v.id === h)?.id ?? null;
  return { view, incident: null, state: null, species: null };
}

function shell(app: HTMLElement, data: Dataset): void {
  const { meta } = data;
  app.innerHTML = `
    <header class="site-header">
      <div class="header-inner">
        <a class="brand" href="#overview">${LOGO}<span>Shark Incidents</span><span class="brand-sub">Australia, ${meta.dataFrom}–${meta.dataThrough}</span></a>
        <div class="search-wrap">
          <input class="search-input" type="search" placeholder="Search a beach, town or species…" aria-label="Search incidents" autocomplete="off" />
          <div class="search-results" role="listbox"></div>
        </div>
        <div class="header-spacer"></div>
        <button class="icon-btn" data-role="about" aria-label="About this site" title="About this site">?</button>
      </div>
    </header>
    <nav class="nav-tabs" aria-label="Views">
      <div class="nav-inner" role="tablist">
        ${VIEWS.map((v) => `<button class="nav-tab" role="tab" data-view="${v.id}" aria-selected="false">${v.label}</button>`).join('')}
      </div>
    </nav>
    <main class="main-content" id="view-root"></main>
    <footer class="site-footer">
      <div class="footer-inner">
        <span>
          Data: <a href="${meta.sources.asid}" target="_blank" rel="noopener">Australian Shark-Incident Database</a>
          (Taronga · Flinders · NSW DPI; <a href="${meta.sources.paper}" target="_blank" rel="noopener">Riley et&nbsp;al. 2022</a>) ·
          boundaries ABS ASGS 2021. ${escapeHtml(String(meta.dataFrom))}–${escapeHtml(String(meta.dataThrough))}.
        </span>
        <span>
          Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> ·
          <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
        </span>
      </div>
    </footer>
  `;

  const viewRoot = app.querySelector('#view-root') as HTMLElement;
  const about = createAbout(meta);
  const drawers = createDrawers(data, (v) => show(v as ViewId));
  let current: ViewId = 'overview';

  const ctx: ViewContext = {
    data,
    openIncident: (id) => drawers.openIncident(id),
    openState: (st) => drawers.openState(st),
    openSpecies: (key) => drawers.openSpecies(key),
    goTo: (v) => show(v),
  };

  function show(id: ViewId, pushHash = true): void {
    const view = VIEWS.find((v) => v.id === id) ?? VIEWS[0];
    drawers.close();
    document.querySelector('.hover-tip')?.classList.remove('visible'); // clear any stale hover tooltip
    current = view.id;
    app.querySelectorAll('[data-view]').forEach((b) => b.setAttribute('aria-selected', String(b.getAttribute('data-view') === current)));
    viewRoot.innerHTML = '';
    try {
      view.render(viewRoot, ctx);
    } catch {
      viewRoot.innerHTML = '<div class="error-box">Something went wrong rendering this view. Try another tab.</div>';
    }
    if (pushHash) history.replaceState(null, '', `#${current}`);
    window.scrollTo({ top: 0 });
  }

  app.querySelector('.nav-inner')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-view]');
    if (btn) show(btn.getAttribute('data-view') as ViewId);
  });
  app.querySelector('[data-role="about"]')?.addEventListener('click', () => about.open());
  app.querySelector('.brand')?.addEventListener('click', (e) => {
    e.preventDefault();
    show('overview');
  });

  // ── Header search ──
  const input = app.querySelector('.search-input') as HTMLInputElement;
  const results = app.querySelector('.search-results') as HTMLElement;
  let hits: SearchHit[] = [];
  let cursor = -1;
  const closeSearch = () => { results.innerHTML = ''; cursor = -1; };
  const paint = () => {
    results.innerHTML = hits
      .map((h, i) => `
        <button class="search-item" data-id="${h.incident.id}" role="option" aria-selected="${i === cursor}">
          <strong>${escapeHtml(h.label)}</strong>
          <span>${escapeHtml(h.sub)}</span>
        </button>`)
      .join('');
  };
  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      hits = searchIncidents(data.incidents, input.value);
      cursor = -1;
      paint();
    }, 300);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { closeSearch(); input.blur(); return; }
    if (!hits.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); cursor = (cursor + 1) % hits.length; paint(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); cursor = (cursor - 1 + hits.length) % hits.length; paint(); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = hits[cursor >= 0 ? cursor : 0];
      if (pick) { drawers.openIncident(pick.incident.id); input.value = ''; closeSearch(); }
    }
  });
  results.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-id]');
    if (!btn) return;
    drawers.openIncident(Number(btn.getAttribute('data-id')));
    input.value = '';
    closeSearch();
  });
  document.addEventListener('click', (e) => {
    if (!(e.target as Element).closest('.search-wrap')) closeSearch();
  });

  // ── Routing ──
  const initial = parseHash();
  show(initial.view ?? 'overview', !(initial.incident != null || initial.state || initial.species));
  if (initial.incident != null) drawers.openIncident(initial.incident);
  else if (initial.state) drawers.openState(initial.state);
  else if (initial.species) drawers.openSpecies(initial.species);
  window.addEventListener('hashchange', () => {
    const { view, incident, state, species } = parseHash();
    if (incident != null) drawers.openIncident(incident);
    else if (state) drawers.openState(state);
    else if (species) drawers.openSpecies(species);
    else if (view && view !== current) show(view, false);
  });
}

function renderError(app: HTMLElement, retry: () => void): void {
  app.innerHTML = `
    <main class="main-content">
      <div class="error-box">
        <strong>Could not load the data.</strong>
        <p style="margin-top:8px">Check your connection and try again.</p>
        <button type="button">Retry</button>
      </div>
    </main>`;
  app.querySelector('button')?.addEventListener('click', retry);
}

async function boot(): Promise<void> {
  const app = document.getElementById('app') as HTMLElement;
  app.innerHTML = `
    <main class="main-content">
      <div class="loading">Loading Australia's shark-incident record…</div>
      <div class="skeleton"></div>
    </main>`;
  initTooltip();
  initGlossary();
  try {
    const data = await loadDataset();
    shell(app, data);
  } catch {
    renderError(app, () => { void boot(); });
  }
}

void boot();

export type { Dataset };
